import React, { useState, useEffect, useMemo } from "react";
import { Briefcase, FolderOpen, PlusIcon } from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import { useAlert } from "../../hooks/useAlert";
import { usePageMeta } from "../../hooks/usePageMeta";
import { PAGE_METADATA } from "../../config/pageMetadata";
import type { Case } from "../../types";

import { Table, TableColumn } from "../../components/common/tables/Table";
import { TableContainer } from "../../components/common/tables/TableContainer";
import {
  LinkCell,
  DateCell,
  TextCell,
} from "../../components/common/tables/cells";
import { CaseStatusBadge, CasePriorityBadge } from "../../utils/caseBadges";
import { useOrg } from "../../contexts/OrgContext.tsx";
import { CaseStatus, CasePriority } from "../../types";
import Button from "../../components/common/ui/button/Button.tsx";

interface CaseDashboardProps {}

type SortField = "status" | "priority" | "updatedAt";
type SortDirection = "asc" | "desc" | null;

const STATUS_ORDER: Record<CaseStatus, number> = {
  [CaseStatus.OPEN]: 1,
  [CaseStatus.IN_PROGRESS]: 2,
  [CaseStatus.CLOSED]: 3,
  [CaseStatus.ARCHIVED]: 4,
};

const PRIORITY_ORDER: Record<CasePriority, number> = {
  [CasePriority.URGENT]: 1,
  [CasePriority.HIGH]: 2,
  [CasePriority.MEDIUM]: 3,
  [CasePriority.LOW]: 4,
};

export const CaseDashboard: React.FC<CaseDashboardProps> = () => {
  usePageMeta(PAGE_METADATA.cases);

  const { setAlert } = useAlert();
  const [loading, setLoading] = useState<boolean>(true);
  const [cases, setCases] = useState<Case[]>([]);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const { orgId, navigateOrg, navigateOrgUrl } = useOrg();

  useEffect(() => {
    if (orgId) {
      loadCases();
    }
  }, [orgId]);

  const loadCases = async () => {
    if (!orgId) return;

    setLoading(true);
    try {
      const casesList = await apiClient.listCases(orgId);
      setCases(casesList);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to load cases",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle through: asc -> desc -> null
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortDirection(null);
        setSortField(null);
      }
    } else {
      setSortField(field as SortField);
      setSortDirection("asc");
    }
  };

  const sortedCases = useMemo(() => {
    if (!sortField || !sortDirection) return cases;

    return [...cases].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case "status":
          comparison = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
          break;
        case "priority":
          comparison = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
          break;
        case "updatedAt":
          comparison =
            new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [cases, sortField, sortDirection]);

  const columns: TableColumn<Case>[] = [
    {
      header: "Name",
      accessor: "title",
      render: (value, row) => (
        <LinkCell
          to={navigateOrgUrl(`/cases/${row.id}`)}
          text={value}
          fallback="Untitled Case"
        />
      ),
    },
    {
      header: "Status",
      accessor: "status",
      sortable: true,
      render: (value) => <CaseStatusBadge status={value} />,
    },
    {
      header: "Priority",
      accessor: "priority",
      sortable: true,
      render: (value) => <CasePriorityBadge priority={value} />,
    },
    {
      header: "Updated At",
      accessor: "updatedAt",
      sortable: true,
      render: (value) => <DateCell date={value} />,
    },
    {
      header: "Record Count",
      accessor: "fileCount",
      render: (_value, row) => (
        <TextCell variant="muted">
          {(row.fileCount || 0) + (row.formCount || 0)}
        </TextCell>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="mb-2 flex items-center gap-3 text-title-md2 font-semibold text-black dark:text-white">
            <Briefcase size={32} />
            Case Management
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Create and manage cases for your organization
          </p>
        </div>
        <Button
          onClick={(evt) => {
            evt.preventDefault();
            console.log("happened");
            navigateOrg("cases/new");
          }}
          disabled={!orgId}
        >
          <PlusIcon size={18} className="mr-2" />
          Create Case
        </Button>
      </div>

      <TableContainer
        loading={loading}
        loadingText="Loading cases..."
        isEmpty={cases.length === 0}
        emptyIcon={<FolderOpen size={48} />}
        emptyTitle="No cases yet"
        emptyDescription="Create your first case to get started"
        title="Cases"
        titleIcon={<FolderOpen size={20} />}
        emptyAction={{
          label: "Create Your First Form Request",
          icon: <PlusIcon size={18} />,
          onClick: () => navigateOrg(`cases/new`),
        }}
        count={cases.length}
      >
        <Table
          columns={columns}
          data={sortedCases}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      </TableContainer>
    </div>
  );
};
